import { formatMoney, formatSignedMoney, TenantCurrency } from 'src/common/currency';
import { atAge, NEVER_RUNS_OUT_TEXT, RUNS_OUT_STARTING_SENTENCE } from './moneyLasts';
import { ChartView, NetWorthBasis, ProjectionSummary } from './types';

export type KpiTone = 'good' | 'warn' | 'bad';
export type Kpi = { label: string; value: string; tone?: KpiTone };

export const resilienceTone = (pct: number): KpiTone => {
  if (pct >= 80) return 'good';
  if (pct >= 50) return 'warn';
  return 'bad';
};

// Projections made before the cut-off could be set ran to 105 and do not say so.
const DEFAULT_HORIZON_AGE = 105;

export const horizonAge = (age: number | null | undefined): number => age ?? DEFAULT_HORIZON_AGE;

const legacyLabel = (view: ChartView, basis: NetWorthBasis, age: number): string => {
  const money = view === 'real' ? "today's money" : 'future money';
  return basis === 'liquid'
    ? `Spendable assets at ${age} (${money})`
    : `Total estate at ${age} (${money})`;
};

export type ValueAtAge = { value: number | null; age: number | null };

// A person aged 104.97 is 104, which is also how the pack and the Siesta digest state it.
const wholeYearsOfAge = (age: number | null | undefined): number | null =>
  age != null ? Math.floor(age) : null;

type HeadlineFigureForView = ValueAtAge & {
  moneyBasis: "today's money" | 'future money';
  todaysMoneyEquivalent: ValueAtAge | null;
};

type HeadlineFiguresForView = {
  peak: HeadlineFigureForView;
  estate: HeadlineFigureForView;
  iht: HeadlineFigureForView;
};

/**
 * Chooses between a figure's two forms. Today's money wins when the projection has it. A stored
 * projection from before today's-money figures existed has none, and the API reports that as
 * null, so null and absent both fall back to future money.
 */
export const chooseHeadlineFigure = (
  view: ChartView,
  futureMoney: ValueAtAge,
  todaysMoney: { value: number | null | undefined; age?: number | null },
): HeadlineFigureForView => {
  const todaysMoneyEquivalent: ValueAtAge | null =
    todaysMoney.value != null
      ? { value: todaysMoney.value, age: wholeYearsOfAge(todaysMoney.age) }
      : null;
  if (view === 'real' && todaysMoneyEquivalent) {
    return { ...todaysMoneyEquivalent, moneyBasis: "today's money", todaysMoneyEquivalent: null };
  }
  return {
    ...futureMoney,
    age: wholeYearsOfAge(futureMoney.age),
    moneyBasis: 'future money',
    todaysMoneyEquivalent,
  };
};

/**
 * The peak, estate and inheritance tax figures for the chart's view, each chosen by its own
 * today's-money field. A figure the projection has only in future money is labelled as such.
 */
export const chooseHeadlineFigures = (
  summary: ProjectionSummary,
  view: ChartView,
): HeadlineFiguresForView => ({
  peak: chooseHeadlineFigure(
    view,
    { value: summary.peak_net_worth_value, age: summary.peak_net_worth_age },
    { value: summary.peak_net_worth_value_real, age: summary.peak_net_worth_age_real },
  ),
  estate: chooseHeadlineFigure(
    view,
    { value: summary.estate_value_at_le, age: null },
    { value: summary.estate_value_at_le_real },
  ),
  iht: chooseHeadlineFigure(
    view,
    { value: summary.iht_due_at_le, age: null },
    { value: summary.iht_due_at_le_real },
  ),
});

/**
 * The figure in today's money, or null when it is absent, reads the same as the headline figure,
 * or the headline figure it would sit beside has no value.
 */
export const todaysMoneyEquivalentText = (
  figure: HeadlineFigureForView,
  describe: (figure: ValueAtAge) => string,
): string | null => {
  const equivalent = figure.todaysMoneyEquivalent;
  if (figure.value == null || equivalent?.value == null) return null;
  const text = describe(equivalent);
  return text === describe(figure) ? null : `${text} in today's money`;
};

const withTodaysMoneyBracket = (
  figure: HeadlineFigureForView,
  describe: (figure: ValueAtAge) => string,
): string => {
  const equivalent = todaysMoneyEquivalentText(figure, describe);
  return equivalent ? `${describe(figure)} (${equivalent})` : describe(figure);
};

const signedMoneyAtAge =
  (currency: TenantCurrency) =>
  ({ value, age }: ValueAtAge): string =>
    `${formatSignedMoney(value ?? 0, currency)}${age != null ? ` at ${age}` : ''}`;

const roundedMoney =
  (currency: TenantCurrency) =>
  ({ value }: ValueAtAge): string =>
    formatMoney(Math.round(value ?? 0), currency);

const moneyRunsOutText = (summary: ProjectionSummary): string => {
  if (summary.runs_out_at_age != null) return atAge(summary.runs_out_at_age);
  return summary.first_shortfall_age != null ? RUNS_OUT_STARTING_SENTENCE : NEVER_RUNS_OUT_TEXT;
};

export const buildKpis = (
  summary: ProjectionSummary,
  view: ChartView,
  currency: TenantCurrency,
  resiliencePct?: number | null,
  basis: NetWorthBasis = 'total',
): Kpi[] => {
  const total = view === 'real' ? summary.legacy_real : summary.legacy_nominal;
  const liquid = view === 'real' ? summary.liquid_legacy_real : summary.liquid_legacy_nominal;
  const legacy = basis === 'liquid' ? (liquid ?? total) : total;
  const headline = chooseHeadlineFigures(summary, view);
  return [
    ...(resiliencePct != null
      ? [
          {
            label: 'Resilience score',
            value: `${Math.round(resiliencePct)}%`,
            tone: resilienceTone(resiliencePct),
          },
        ]
      : []),
    {
      label: legacyLabel(view, basis, horizonAge(summary.horizon_age)),
      value: legacy != null ? formatSignedMoney(legacy, currency) : '—',
    },
    {
      label: `Peak net worth (${headline.peak.moneyBasis})`,
      value:
        headline.peak.value != null
          ? withTodaysMoneyBracket(headline.peak, signedMoneyAtAge(currency))
          : '—',
    },
    {
      label: 'Money runs out',
      value: moneyRunsOutText(summary),
    },
    {
      label: 'Gross income, first year',
      value:
        summary.total_income_first_year != null
          ? formatMoney(summary.total_income_first_year, currency)
          : '—',
    },
    {
      label: 'Spending, first year',
      value:
        summary.total_expenditure_first_year != null
          ? formatMoney(summary.total_expenditure_first_year, currency)
          : '—',
    },
    ...(headline.estate.value != null
      ? [
          {
            label: `Estate at life expectancy (${headline.estate.moneyBasis})`,
            value: withTodaysMoneyBracket(headline.estate, roundedMoney(currency)),
          },
        ]
      : []),
    ...(headline.iht.value != null
      ? [
          {
            label: `IHT at life expectancy (${headline.iht.moneyBasis})`,
            value: withTodaysMoneyBracket(headline.iht, roundedMoney(currency)),
            tone: headline.iht.value > 0 ? ('warn' as const) : ('good' as const),
          },
        ]
      : []),
  ];
};
